import { useQuery } from "@tanstack/react-query";
import { shoppingAccessQuery } from "@/shared/shoppingQueries.ts";
import { iconForName } from "@/components/artifacts/iconForName.ts";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { Button } from "@/components/ui/button.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Link } from "wouter";

type LinkButtonComponentData = Extract<ArtifactComponent, { type: "linkButton" }>;

export const LinkButtonBlock = ({ component }: { component: LinkButtonComponentData }) =>
  component.requiresShoppingPayments ? (
    <ShoppingLink component={component} />
  ) : (
    <LinkButton component={component} />
  );
const ShoppingLink = ({ component }: { component: LinkButtonComponentData }) => {
  const { isSharedThread } = useArtifactViewCtx();
  const access = useQuery({ ...shoppingAccessQuery(), enabled: !isSharedThread });
  return access.data?.enabled === true &&
    access.data.paymentsEnabled === true &&
    !isSharedThread ? (
    <LinkButton component={component} />
  ) : null;
};
const LinkButton = ({ component }: { component: LinkButtonComponentData }) => {
  const { isSharedThread } = useArtifactViewCtx();
  // Session-authenticated links 401 on shared threads — omit the button.
  if (component.requiresAuth && isSharedThread) return null;
  const Icon = component.icon ? iconForName(component.icon) : null;
  const button = (
    <Button variant="ghost" size="xs" data-statsig-name={`artifact-view-link-${component.id}`}>
      {Icon ? <Icon className="w-4 h-4 mr-2" /> : null}
      {component.label}
    </Button>
  );

  // In-app route → SPA navigation (no reload); otherwise an external/download href.
  if (component.appLink) {
    return <Link href={component.appLink}>{button}</Link>;
  }
  if (!component.href) return null;
  return (
    <a
      href={resolveApiPath(component.href)}
      target={component.openIn === "external" ? "_blank" : undefined}
      rel={component.openIn === "external" ? "noopener noreferrer" : undefined}
    >
      {button}
    </a>
  );
};
